import React, { ChangeEvent, FC, useCallback, useMemo } from 'react';
import { Box, Checkbox, FormControlLabel } from '@mui/material';
import { Showable, useStyles } from '@sweatpals/react-ui-kit';
import { CheckRounded } from '@mui/icons-material';
import { findKey, get, isNumber, reduce } from 'lodash-es';
import { useFormikContext } from 'formik';
import { CoreTextField } from '../core-textfield';
import { CorePhoneInput } from '../core-phone-input';
import IntakeForm from '../forms/intake-form/intake-form.component';
import {
	EventPriceDto,
	IntakeFormValue,
	IntakeFormReservedFieldNames,
	IntakeFormSectionField,
	EventIntakeFormSettings,
	IntakeFormOtherFields,
} from '@sweatpals/shared';
import {
	CheckoutFormDto,
	EventPricesStepsDto,
	IntakeFormDto
} from '../../../interfaces/checkout-form.dto';
import { EventType } from '../../../services/types/event-types';
import { commonCheckoutFormStyles } from './checkout-form.styles';
import { useAnalytics } from 'hooks/use-analytics.hook';

export const getPrefilledIntakeFormResults = ({
	isPrefilled,
	defaultIntakeForm,
	firstIntakeForm,
	currentEventPrice,
	intakeFormFields,
}: {
	isPrefilled: boolean;
	defaultIntakeForm: IntakeFormDto;
	firstIntakeForm?: IntakeFormDto;
	currentEventPrice?: EventPriceDto;
	intakeFormFields: IntakeFormSectionField[];
}) => {
	const intakeFormResult = isPrefilled && firstIntakeForm?.result ? firstIntakeForm.result : defaultIntakeForm.result;

	// other important fields that are not included into intakeFormFields or IntakeFormReservedFieldNames
	const initialValue: IntakeFormOtherFields = {
		waiverAgreement: intakeFormResult.waiverAgreement,
		participantInitials: intakeFormResult.participantInitials,
		skipSigningForm: intakeFormResult.skipSigningForm,
		userId: intakeFormResult.userId,
	};

	const preparedIntakeFormResult = reduce(intakeFormResult, (acc, value, key) => {
		const field = intakeFormFields.find(f => f.name === key);

		if (!currentEventPrice || !field?.tierIds || field.tierIds.includes(currentEventPrice.id)) {
			return { ...acc, [key]: value };
		}

		return acc;
	}, initialValue);

	return preparedIntakeFormResult;
};

export interface TicketFormProps {
	checkoutStep: number;
	paymentStep: number;
	currentIntakeFormIndex: number;
	eventData: EventType;
	eventPricesSteps: EventPricesStepsDto;
	intakeFormFields: IntakeFormSectionField[];
	firstIntakeForm?: IntakeFormDto;
	defaultIntakeForm: IntakeFormDto;
	disabled?: boolean;
	intakeFormSettings?: EventIntakeFormSettings;
	currentEventPrice?: EventPriceDto;
}

export const TicketForm: FC<TicketFormProps> = (props) => {
	const {
		eventData,
		checkoutStep,
		paymentStep,
		intakeFormFields,
		intakeFormSettings,
		currentEventPrice,
		eventPricesSteps,
		firstIntakeForm,
		defaultIntakeForm,
		currentIntakeFormIndex,
		disabled,
	} = props;

	const {
		values,
		errors,
		setValues,
		setFieldValue,
		handleChange,
		validateField,
	} = useFormikContext<CheckoutFormDto>();
	const commonStyles = useStyles(commonCheckoutFormStyles)

	const { logEventTyped } = useAnalytics()

	const intakeFormFieldNames = useMemo(() => intakeFormFields.map(f => f.name), [intakeFormFields]);
	const intakeFormCommonFieldNames = useMemo(
		() => intakeFormFields.filter(f => f.isCommonForAllTickets).map(f => f.name),
		[intakeFormFields]
	);
	const currentIntakeForm = useMemo(
		() => values.intakeForms?.[currentIntakeFormIndex],
		[currentIntakeFormIndex, values.intakeForms],
	);
	const intakeFormReservedFieldNames = useMemo<string[]>(() => [
		IntakeFormReservedFieldNames.FullName,
		IntakeFormReservedFieldNames.Email,
		IntakeFormReservedFieldNames.PhoneNumber,
	], []);
	const currentIntakeFormErrors = useMemo(() => {
		const error = errors.intakeForms?.[currentIntakeFormIndex];

		if (!error || typeof error === 'string') {
			return;
		}

		return error;
	}, [currentIntakeFormIndex, errors.intakeForms]);

	const onIsPrefilledChange = useCallback(
		(_e: ChangeEvent<HTMLInputElement>, isPrefillChecked: boolean): void => {
			const preparedIntakeFormResult = getPrefilledIntakeFormResults({
				isPrefilled: isPrefillChecked,
				defaultIntakeForm,
				firstIntakeForm,
				currentEventPrice,
				intakeFormFields,
			});

			setFieldValue(`intakeForms.[${currentIntakeFormIndex}].isPrefilled`, isPrefillChecked);
			setFieldValue(`intakeForms.[${currentIntakeFormIndex}].result`, preparedIntakeFormResult);
		},
		[
			getPrefilledIntakeFormResults,
			setFieldValue,
			currentIntakeFormIndex,
			currentEventPrice,
			defaultIntakeForm,
			firstIntakeForm,
			intakeFormFields,
		]
	);

	const changeIntakeFormValues = useCallback((key: string, value?: IntakeFormValue) => {
		const isCommonField = intakeFormCommonFieldNames.includes(key);
		const field = intakeFormFields.find(f => f.name === key);

		setValues({
			...values,
			intakeForms: values.intakeForms?.map((intakeForm, index) => {
				const intakeFormStep = index + 1;

				const eventPriceId = findKey(
					eventPricesSteps,
					({ firstStep, lastStep }) => (firstStep <= intakeFormStep && intakeFormStep <= lastStep),
				);

				const isEditable = (index === currentIntakeFormIndex || isCommonField)
					&& (!eventPriceId || !field?.tierIds || field.tierIds.includes(eventPriceId));

				return {
					...intakeForm,
					result: {
						...intakeForm.result,
						[key]: isEditable ? value : intakeForm.result[key],
					}
				};
			})
		});
	}, [intakeFormCommonFieldNames, intakeFormFields, setValues, values, eventPricesSteps, currentIntakeFormIndex]);

	const handleChangePhone = useCallback((phone: string): void => {
		setFieldValue('phone', phone);

		if (intakeFormFieldNames.includes(IntakeFormReservedFieldNames.PhoneNumber)) {
			setFieldValue(`intakeForms.[0].result.["${IntakeFormReservedFieldNames.PhoneNumber}"]`, phone);
		}
	}, [intakeFormFieldNames, setFieldValue]);

	const handleChangeFullName = useCallback((e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		handleChange(e);

		if (intakeFormFieldNames.includes(IntakeFormReservedFieldNames.FullName)) {
			setFieldValue(
				`intakeForms.[0].result.["${IntakeFormReservedFieldNames.FullName}"]`,
				e.target.value
			);
		}
	}, [handleChange, intakeFormFieldNames, setFieldValue]);

	const handleChangeEmail = useCallback((e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		handleChange(e);

		if (intakeFormFieldNames.includes(IntakeFormReservedFieldNames.Email)) {
			setFieldValue(
				`intakeForms.[0].result.["${IntakeFormReservedFieldNames.Email}"]`,
				e.target.value
			);
		}
	}, [handleChange, intakeFormFieldNames, setFieldValue]);

	const handleFieldOnBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		if (event.target.name) {
			void validateField(event.target.name);

			const fieldValue = get(values, event.target.name) as string | undefined;
			const fieldError = get(errors, event.target.name) as string | undefined;

			// track event when user fills the field
			logEventTyped('registration_field_filled', {
				fieldName: event.target.name,
				fieldValue,
				validationError: fieldError,
			});
		}
	};

	return (
		<Box display="flex" flexDirection="column" gap="24px">
			<Showable show={checkoutStep > 1 && checkoutStep < paymentStep}>
				<FormControlLabel
					sx={commonStyles.checkBox}
					label="Same individual buying ticket"
					control={
						<Checkbox
							disabled={disabled}
							checked={currentIntakeForm?.isPrefilled}
							onChange={onIsPrefilledChange}
							checkedIcon={<CheckRounded />}
						/>
					}
				/>
			</Showable>
			<Showable show={checkoutStep === 1}>
				<CoreTextField
					controlId="fullName"
					id="fullName-input"
					value={values.fullName}
					name="fullName"
					label="Full name"
					placeholder="Full name"
					requiredMark
					disabled={disabled}
					onChange={handleChangeFullName}
					onBlur={handleFieldOnBlur}
					error={!!errors.fullName}
					helperText={errors.fullName}
				/>
				<CoreTextField
					controlId="email"
					id="email-input"
					value={values.email}
					name="email"
					label="Email"
					placeholder="Enter email"
					requiredMark
					disabled={disabled}
					onChange={handleChangeEmail}
					onBlur={handleFieldOnBlur}
					error={!!errors.email}
					helperText={errors.email}
				/>
				<CorePhoneInput
					controlId="phone"
					id="phone-input"
					placeholder="Phone Number"
					label="Phone Number"
					requiredMark
					name="phone"
					disabled={disabled}
					value={values.phone}
					defaultCountry="US"
					forceCallingCode
					MenuProps={{ disableAutoFocusItem: true }}
					onChange={handleChangePhone}
					onBlur={handleFieldOnBlur}
					error={!!errors.phone}
					helperText={errors.phone}
				/>
			</Showable>
			<Showable show={intakeFormSettings || eventData.waiverUrl}>
				<IntakeForm
					baseEventId={eventData.baseId}
					eventInstance={eventData.instance}
					disabled={disabled || currentIntakeForm?.isPrefilled}
					disabledWaiver={disabled || currentIntakeForm?.isPrefilled}
					intakeFormSettings={intakeFormSettings}
					eventPriceId={currentEventPrice?.id}
					waiverUrl={eventData.waiverUrl}
					hiddenFieldNames={
						checkoutStep === 1
							? intakeFormReservedFieldNames
							: currentIntakeForm.isPrefilled
								? intakeFormCommonFieldNames
								: intakeFormFieldNames.filter(name => !intakeFormReservedFieldNames.includes(name))
					}
					hideWaiver={checkoutStep !== 1 && !currentIntakeForm.isPrefilled}
					intakeFormValues={currentIntakeForm?.result}
					intakeFormErrors={currentIntakeFormErrors?.result}
					changeIntakeFormValues={changeIntakeFormValues}
					onBlur={(e) => {
						if (isNumber(currentIntakeFormIndex) && e.target.name && get(values, `intakeForms.[${currentIntakeFormIndex}].result`)) {
							validateField(`intakeForms.[${currentIntakeFormIndex}].result.["${e.target.name}"]`);
						}
					}}
				/>
			</Showable>
		</Box>
	);
};
